import { api, ui, utils } from "services";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Popup } from "semantic-ui-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { removeAuthInfo, setAuthInfos } from 'reducer/wallet.slice';
import { AuthEntity, AuthInterface, LoginType, Size } from 'interfaces';
import CustomButton from '../button';
import { RootState } from 'reducer/store';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Config, { SSO_META } from "config";
import { setLoginType } from "reducer/logintype.slice";
import { getExplorer } from "services/contract";

const getImage = (loginType: LoginType) => {
    switch (loginType) {
        case LoginType.Aura:
            return "/assets/images/wallets/aura.png";
    }
};

const getMainToken = (loginType: LoginType) => {
    switch (loginType) {
        case LoginType.Aura:
            return {
                name: "AURA",
            }
    }
}

export const KeplrWallet = ({
    data,
    size,
    className,
    type
}: {
    data: AuthEntity,
    size: Size,
    className?: string,
    type: LoginType
}) => {
    const [isCopied, setIsCopied] = useState(false);
    const [bank, setBank] = useState(null);
    const [bankField, setBankField] = useState(null);
    const dispatch = useDispatch();
    const wallets = useSelector((state: RootState) => state.wallets);
    const loginType = useSelector((state: RootState) => state.logintype);
    const { t } = useTranslation();
    let userInfo = useSelector((state3: RootState) => state3.user);
    const [mainTokenPrice, setMainTokenPrice] = useState(0)
    const tokenInformation = getMainToken(type);

    const checkExtension = () => {
        //@ts-ignore
        return !!window.keplr;
    }

    const connectExtension = async () => {
        await new Promise((resolve) => {
            ui.confirm(
              "To avoid future risks, you need to confirm that you are using a wallet with a RECOVERY PHRASE to link to your current account.",
              resolve

            );
          });
        //@ts-ignore
        if (!window.keplr) {
            window.open(Config.KeplrExtension, "_blank")
        }
        else {
            try {
                await api.checkNetwork(type, "keplr.tsx");
                const { nonce, sign } = await api.getKeplrSign();
                await api.linkWallet({
                    nonce,
                    sign,
                    type,
                });
                const authInfos = await api.getAuthInfos();
                dispatch(setAuthInfos(authInfos));
                if (Config.AntiWalletAuth.includes(loginType)) {
                    dispatch(setLoginType(type))
                }
            } catch (err: any) {
                ui.alertFailed("", t(err.message))
                let availableAuths = utils.getAvailableAuths(wallets, type);
                await api.checkNetwork(availableAuths[0], "keplr.tsx")
                dispatch(setLoginType(availableAuths[0]))
            }
        }
    }
    const disconnectExtension = async () => {
        ui.alertFailed("Unsupport this function", "");
        return;
    }

    const getMainTokenBalance = async () => {
        if (!data[AuthInterface.identity]) return;
        try {
            let value = await api.getMainBalanceChain(data[AuthInterface.identity], type)
            if (type === loginType) {
                setBank(value);
            } else {
                setBank(null);
            }
        } catch (err) {
            console.log({ err })
        }
    }

    const getMainTokenPrice = async () => {
        let priceUrl;
        switch (type) {
            case LoginType.Aura:
                priceUrl = SSO_META().price.AURA;
                setBankField("aura_wallet");
                break;
            default:
                return;
        }
        const rate = await axios.get(priceUrl);
        const price = rate?.data?.market_data?.current_price?.usd ?? 0;
        setMainTokenPrice(price);
    }

    useEffect(() => {
        if ([LoginType.Aura].includes(type)) {
            getMainTokenPrice();
            getMainTokenBalance();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, loginType])

    switch (size) {
        case Size.Big:
            return (
                <div className={`${className} w-full rounded-lg ${Config.MainnetUnsupportedChain.includes(type) && "disabled-extension"}`}
                    style={{
                        background: `url("/assets/images/wallets/${LoginType[type].toLowerCase()}-wallet.png")`,
                        backgroundRepeat: "no-repeat",
                        position: "relative",
                        backgroundSize: "cover",
                        minHeight: "180px",
                        backgroundPosition: "bottom right"
                    }}
                >
                    <div className="h-36 md:h-28 p-4 px-6">
                        <div className="flex justify-between">
                            <div>Keplr Wallet</div>
                            <div className="flex float-right">
                                <Popup position="bottom center"
                                    content={isCopied ? "Copied" : data[AuthInterface.identity] ? data[AuthInterface.identity] : "Copy"}
                                    onClose={() => setIsCopied(false)}
                                    disabled={!data[AuthInterface.identity]}
                                    trigger={
                                        <CopyToClipboard
                                            text={data[AuthInterface.identity]}
                                            onCopy={() => {
                                                setIsCopied(true)
                                            }}
                                        >
                                            <img
                                                src="/assets/images/icons/copy-icon.svg" alt="copy"
                                                className="h-6 w-6 cursor-pointer" />
                                        </CopyToClipboard>
                                    } />
                                {data[AuthInterface.identity] && <Popup position="bottom center" content={"View on Explore "}
                                    disabled={!data[AuthInterface.identity]}
                                    trigger={
                                        <img
                                            src="/assets/images/icons/open-icon.svg"
                                            alt="open"
                                            className="h-6 w-6 ml-2 cursor-pointer"
                                            onClick={() => {
                                                if (checkExtension() && data[AuthInterface.identity]) {
                                                    window.open(utils.getExplorerUrl({
                                                        address: data[AuthInterface.identity],
                                                        loginType: type,
                                                        testnet: Config.ProductionMode !== "true"
                                                    }), "_blank")
                                                }
                                            }}
                                        />
                                    }>
                                </Popup>}
                                {!data[AuthInterface.identity] && <Popup
                                    position="bottom center" content={data[AuthInterface.identity] ?
                                        `Unlink ${LoginType[type]}` : `Attach ${LoginType[type]}`}
                                    disabled={!data[AuthInterface.identity]}
                                    trigger={
                                        <img
                                            src={`/assets/images/icons/${data[AuthInterface.identity] ? "link-wallet" : "unlink-wallet"}-icon.svg`}
                                            alt="wallet-icon"
                                            className="h-6 w-6 ml-2 cursor-pointer"
                                            onClick={data[AuthInterface.identity] ? disconnectExtension : connectExtension}
                                        />
                                    }>
                                </Popup>}
                            </div>
                        </div>

                        <p className="font-normal mt-2 text-white-light text-lg">
                            {utils.getDisplayLoginType(type)} address: {" "}
                            {utils.displayWalletAddress(data[AuthInterface.identity])}
                        </p>
                        <div className="block my-2 bottom-10 absolute">
                            <span className="font-medium text-2xl">
                                {data[AuthInterface.identity] && bankField && userInfo &&
                                    // @ts-ignore
                                    userInfo[bankField] ? utils.convertToInternationalCurrencySystem(
                                        // @ts-ignore
                                        userInfo[bankField], 8) ?? "--" : "--"} {tokenInformation.name}
                            </span>
                        </div>
                        <div className="justify-between bottom-6 absolute">
                            <span className="font-normal text-white-light">$
                                {(data[AuthInterface.identity] && bankField && userInfo &&
                                    // @ts-ignore
                                    userInfo[bankField]) ?
                                    // @ts-ignore
                                    utils.convertToInternationalCurrencySystem(userInfo[bankField] * mainTokenPrice, 8) : "--"}
                            </span>
                        </div>
                    </div>
                </div >
            )

        case Size.Small:
            return (
                <div className={`w-full border-2 border-gray hover-bg-linear-yellow px-10 ${Config.MainnetUnsupportedChain.includes(type) && "disabled-extension"}`}>
                    <div className="grid grid-cols-1 md:grid md:grid-cols-10 gap-4 py-6">
                        <div className="flex gap-4 col-span-6">
                            <img src={getImage(type)} className="mt-1 w-8 h-8 text-center" alt="keplr" />
                            <div className="grid">
                                <div className="row-span-1 text-sm text-gray">Keplr wallet</div>
                                <div className="grid grid-cols-8 row-span-1 text-white-light main-font-light">
                                    <div className="font-normal col-span-6">{utils.getDisplayLoginType(type)} address:
                                        {utils.displayWalletAddress(data[AuthInterface.identity])}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex ml-auto gap-4 self-center col-span-4">
                            <div className="flex mr-2 gap-2 h-full">
                                <Popup position="bottom center" content={
                                    <div className="popup-text" style={{
                                        pointerEvents: "none"
                                    }}>
                                        {isCopied ? "Copied" : data[AuthInterface.identity] ? utils.displayWalletAddress(data[AuthInterface.identity]) : "Copy"}
                                    </div>
                                }
                                    onClose={() => setIsCopied(false)}
                                    disabled={!data[AuthInterface.identity]}
                                    trigger={
                                        <CopyToClipboard
                                            text={data[AuthInterface.identity]}
                                            onCopy={() => {
                                                setIsCopied(true)
                                            }}
                                        >
                                            <img
                                                src="/assets/images/icons/copy-icon.svg" alt="copy"
                                                className="h-8 w-8 cursor-pointer" />
                                        </CopyToClipboard>
                                    } />
                                {data[AuthInterface.identity] && <Popup position="bottom center" content={"View on Explore "}
                                    disabled={!data[AuthInterface.identity]}
                                    trigger={
                                        <img
                                            src="/assets/images/icons/open-icon.svg"
                                            alt="open"
                                            className="h-8 w-8 cursor-pointer"
                                            onClick={() => {
                                                if (checkExtension() && data[AuthInterface.identity]) {
                                                    let url = getExplorer(utils.getChainFromLogin(type)) + "/address/" + data[AuthInterface.identity];
                                                    window.open(url, "_blank")
                                                }
                                            }}
                                        />
                                    }>
                                </Popup>}
                            </div>
                            <div className="">
                                {checkExtension() ?
                                    (data[AuthInterface.identity] ? (([LoginType.Aura].includes(loginType)) && loginType === type) ?
                                        <CustomButton
                                            className="w-[100px] h-full"
                                        >
                                            <span className='text-sm text-orange'>Connected</span>
                                        </CustomButton> : <CustomButton
                                            color="orange"
                                            className="w-[100px] h-full"
                                            onClick={async () => {
                                                await api.checkNetwork(type, "keplr.tsx");
                                                dispatch(setLoginType(type))
                                            }}
                                        >
                                            <span className='text-sm text-orange'>Switch</span>
                                        </CustomButton> :
                                        <CustomButton
                                            className="w-[100px] h-full"
                                            color="blue"
                                            onClick={connectExtension}
                                        >
                                            <span className='text-sm'>Attach</span>
                                        </CustomButton>) :
                                    <CustomButton
                                        className="w-[100px] h-full"
                                        color="purple"
                                        onClick={connectExtension}
                                    >
                                        <span className='text-sm'>Install</span>
                                    </CustomButton>
                                }
                            </div>
                        </div>
                    </div>
                </div >
            )
    }
}
